
import React, { useState } from 'react';
import { 
  LayoutDashboard, 
  Users, 
  Settings, 
  Power, 
  ListChecks,
  FileCode2,
  LayoutList,
  Menu,
  X,
  UserCircle
} from 'lucide-react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface VerticalMenuProps {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

const VerticalMenu: React.FC<VerticalMenuProps> = ({ theme, toggleTheme }) => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  
  const menuItems = [
    { path: '/teams', icon: <Users />, label: 'Teams' },
    { path: '/policies', icon: <ListChecks />, label: 'Policies' },
    { path: '/policy-editor', icon: <FileCode2 />, label: 'Policy Editor' },
    { path: '/powerbi', icon: <LayoutList />, label: 'Power BI' },
    { path: '/settings', icon: <Settings />, label: 'Settings' },
    { path: '/logs', icon: <LayoutDashboard />, label: 'Logs' },
  ];
  
  const handleLogout = async () => {
    await signOut();
    navigate('/auth');
  };
  
  const getAvatarInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  // Mobile menu toggle
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  return (
    <>
      {/* Mobile menu toggle button */}
      {isMobile && (
        <Button 
          variant="ghost" 
          size="icon" 
          className="fixed top-4 left-4 z-[60] bg-secondary/80 backdrop-blur-sm"
          onClick={toggleMenu}
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      )}
    
      <TooltipProvider delayDuration={50}>
        <div className={cn(
          "fixed top-0 left-0 h-full w-[100px] bg-secondary border-r border-r-border z-50 flex flex-col items-center p-3 transition-all duration-300",
          isMobile ? (menuOpen ? "translate-x-0" : "translate-x-[-100%]") : "translate-x-0"
        )}>
          {/* Logo */}
          <div className="mb-6">
            <NavLink to="/teams" onClick={() => isMobile && setMenuOpen(false)}>
              <h1 className="text-xl font-bold text-primary">TeamHub</h1>
            </NavLink>
          </div>
          
          {/* Menu Items */}
          <div className="flex-grow flex flex-col items-center space-y-1">
            {menuItems.map((item) => (
              <Tooltip key={item.label}>
                <TooltipTrigger asChild>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `relative flex items-center justify-center h-12 w-12 rounded-lg hover:bg-primary/10 transition-colors duration-200
                      ${isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`
                    }
                    onClick={() => isMobile && setMenuOpen(false)}
                  >
                    {item.icon}
                    {location.pathname === item.path && (
                      <span className="absolute left-0 top-1/2 transform -translate-y-1/2 h-8 w-[2px] bg-primary rounded-r-md"></span>
                    )}
                  </NavLink>
                </TooltipTrigger>
                <TooltipContent side={isMobile ? "right" : "right"}>{item.label}</TooltipContent>
              </Tooltip>
            ))}
          </div>
          
          {/* User Info and Theme Toggle */}
          <div className="flex flex-col items-center space-y-4 mt-6 w-full">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={user?.user_metadata?.avatar_url || ''} alt={user?.email || 'User'} />
                    <AvatarFallback>
                      {user?.user_metadata?.avatar_url ? null : <UserCircle className="h-5 w-5" />}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </TooltipTrigger>
              <TooltipContent className="text-xs">{user?.email || 'User'}</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <Switch id="theme" onClick={toggleTheme} checked={theme === 'dark'} />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="text-xs">Toggle Theme</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9" onClick={handleLogout}>
                  <Power className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="text-xs">Logout</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </TooltipProvider>
      
      {/* Mobile overlay */}
      {isMobile && menuOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          onClick={() => setMenuOpen(false)}
        />
      )}
    </>
  );
};

export default VerticalMenu;
